import { useEffect, useMemo, useState } from 'react'
import { Dialog } from './Dialog'
import { SearchPick } from './SearchPick'
import { useHubLang } from '../lib/i18n'
import { SITE_LIST } from '../lib/hubCopy'
import { messageOf } from '../lib/errors'
import { matchOptions } from '../lib/searchPick'
import { adminSites, mergeSite, renameSite, retireSite, type AdminSite } from '../lib/photoRoom'

/**
 * 321 (FED-74). The Gallery admin's site list, beside the tag list.
 *
 * Sites come from OneConetec proposing them and people confirming them
 * (David, 29 Oct: "One proposes, the list grows"). What an admin does here
 * is keep the list clean: rename a name that is spelled wrong, merge two that
 * are the same place (every photo of one moves to the other), retire one that
 * should not be offered (OneConetec's guesses of it are cleared; a photo
 * somebody confirmed keeps it). A proposal nobody has confirmed is shown, so
 * a wrong one can be retired before anybody is offered it. The server checks
 * photo.location for every change (rename/merge/retire_photo_site).
 */
export function SiteListTray({ onClose }: { onClose: () => void }) {
  const t = SITE_LIST[useHubLang()]
  const [rows, setRows] = useState<AdminSite[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [rev, setRev] = useState(0)

  useEffect(() => {
    let live = true
    adminSites()
      .then((r) => live && setRows(r))
      .catch(() => live && setError(t.couldNotLoad))
    return () => {
      live = false
    }
  }, [rev, t.couldNotLoad])

  const shown = useMemo(
    () => matchOptions((rows ?? []).map((r) => ({ ...r, label: r.name })), filter, 500),
    [rows, filter],
  )

  return (
    <Dialog open variant="tray" title={t.title} onClose={onClose} className="taglist sitelist">
      <p className="taglist__intro">{t.intro}</p>
      {error && (
        <p className="taglist__notice" role="status">
          {error}
        </p>
      )}
      {rows && rows.length > 0 && (
        <input
          type="search"
          className="sitelist__filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t.filter}
          aria-label={t.filter}
          maxLength={120}
        />
      )}
      {rows && rows.length === 0 && <p className="taglist__intro">{t.none}</p>}
      {rows && rows.length > 0 && (
        <ul className="taglist__rows">
          {shown.map((r) => (
            <SiteRow key={r.id} row={r} all={rows} onChanged={() => setRev((x) => x + 1)} />
          ))}
        </ul>
      )}
    </Dialog>
  )
}

function SiteRow({ row, all, onChanged }: { row: AdminSite; all: AdminSite[]; onChanged: () => void }) {
  const t = SITE_LIST[useHubLang()]
  const [mode, setMode] = useState<'idle' | 'rename' | 'merge' | 'retire'>('idle')
  const [name, setName] = useState(row.name)
  const [into, setInto] = useState<AdminSite | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const others = useMemo(
    () => all.filter((s) => s.id !== row.id && !s.retired && s.tenantId === row.tenantId).map((s) => ({ ...s, label: s.name })),
    [all, row],
  )

  async function act(fn: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      setMode('idle')
      setInto(null)
      onChanged()
    } catch (e) {
      setError(messageOf(e) || t.failed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className="taglist__row">
      <div className="taglist__name">
        {row.name}
        {!row.listed && <span className="sitelist__badge">{t.proposed}</span>}
        {row.retired && <span className="sitelist__badge sitelist__badge--off">{t.retired}</span>}
      </div>
      <div className="taglist__record">{t.counts(row.accepted, row.suggested)}</div>
      {mode === 'idle' && (
        <div className="sitelist__actions">
          <button type="button" className="btn btn--link" onClick={() => { setName(row.name); setMode('rename') }}>
            {t.rename}
          </button>
          {!row.retired && others.length > 0 && (
            <button type="button" className="btn btn--link" onClick={() => setMode('merge')}>
              {t.merge}
            </button>
          )}
          {!row.retired && (
            <button type="button" className="btn btn--link" onClick={() => setMode('retire')}>
              {t.retire}
            </button>
          )}
        </div>
      )}
      {mode === 'rename' && (
        <div className="taglist__edit">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} aria-label={t.rename} autoFocus />
          <button type="button" className="btn btn--primary btn--sm" disabled={busy || name.trim().length < 2}
            onClick={() => void act(() => renameSite(row.id, name.trim()))}>
            {t.save}
          </button>
          <button type="button" className="btn btn--quiet btn--sm" onClick={() => setMode('idle')}>
            {t.cancel}
          </button>
        </div>
      )}
      {mode === 'merge' && !into && (
        <div className="taglist__edit">
          <SearchPick options={others} label={t.mergePick} autoFocus onPick={(o) => setInto(o)} />
          <button type="button" className="btn btn--quiet btn--sm" onClick={() => setMode('idle')}>
            {t.cancel}
          </button>
        </div>
      )}
      {mode === 'merge' && into && (
        <div className="sitelist__confirm">
          <p className="taglist__status">{t.mergeConfirm(row.name, into.name)}</p>
          <button type="button" className="btn btn--primary btn--sm" disabled={busy}
            onClick={() => void act(() => mergeSite(row.id, into.id))}>
            {t.mergeYes}
          </button>
          <button type="button" className="btn btn--quiet btn--sm" onClick={() => { setInto(null); setMode('idle') }}>
            {t.cancel}
          </button>
        </div>
      )}
      {mode === 'retire' && (
        <div className="sitelist__confirm">
          <p className="taglist__status">{t.retireConfirm(row.name)}</p>
          <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => void act(() => retireSite(row.id))}>
            {t.retireYes}
          </button>
          <button type="button" className="btn btn--quiet btn--sm" onClick={() => setMode('idle')}>
            {t.cancel}
          </button>
        </div>
      )}
      {error && (
        <p className="taglist__notice" role="alert">
          {error}
        </p>
      )}
    </li>
  )
}
